/**
 * Dynamic JS hover enhancement: a cursor-following radial glow on cards and a
 * pressed-state ripple on buttons. Delegated listeners (one set), so it works
 * for cards rendered later by React. CSS consumes the --mx/--my variables.
 */
export function initInteractiveHover(): void {
  if (typeof document === "undefined") return;

  // Cursor-follow glow: update CSS vars on the hovered card.
  document.addEventListener(
    "pointermove",
    (e) => {
      const target = e.target as HTMLElement | null;
      const card = target?.closest<HTMLElement>(".card");
      if (!card) return;
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      card.style.setProperty("--mx", `${px * 100}%`);
      card.style.setProperty("--my", `${py * 100}%`);
      card.classList.add("glow");
      // Subtle 3D tilt for interactive cards (Discover). Max ~5deg each axis.
      if (card.classList.contains("disc-interactive")) {
        const rx = (0.5 - py) * 8; // rotateX
        const ry = (px - 0.5) * 8; // rotateY
        card.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
        card.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
        card.classList.add("tilt");
      }
    },
    { passive: true },
  );
  document.addEventListener(
    "pointerout",
    (e) => {
      const card = (e.target as HTMLElement | null)?.closest<HTMLElement>(".card");
      if (card && !card.contains((e as PointerEvent).relatedTarget as Node)) {
        card.classList.remove("glow");
        card.classList.remove("tilt");
        card.style.removeProperty("--rx");
        card.style.removeProperty("--ry");
      }
    },
    { passive: true },
  );

  // Button ripple on press.
  document.addEventListener("pointerdown", (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      "button, .btn",
    );
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const ripple = document.createElement("span");
    ripple.className = "ripple";
    const size = Math.max(r.width, r.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - r.left - size / 2}px`;
    ripple.style.top = `${e.clientY - r.top - size / 2}px`;
    btn.appendChild(ripple);
    ripple.addEventListener("animationend", () => ripple.remove());
  });
}
